import { useCallback, useEffect } from 'react';
import { ui } from '@procuros/datachecks';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { isWebEDIFormError, WebEDIAnnotation } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { FieldState } from 'support/types/ui';
import { useInterpolatedEnumerableOptions } from 'components/SchemableForm/hooks/useInterpolatedEnumerableOptions';
import { Translate } from 'components/Translate/Translate';

import { Radio } from '../Radio/Radio';

type SchemableFormRadioGroupProps = {
  field: ui.RadioField;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  error: WebEDIAnnotation | undefined;
  onShowFieldErrorModal?: (showFieldErrorModal: boolean) => void;
  required: boolean;
  disabled: boolean;
  fieldState: FieldState;
  variant?: 'large';
  name: string;
};

export const SchemableFormRadioGroup = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  onShowFieldErrorModal,
  required,
  disabled,
  fieldState,
  variant,
  name,
}: SchemableFormRadioGroupProps) => {
  const { options } = useInterpolatedEnumerableOptions(field.type_config);
  const isValidating = useWebEDIStore((state) => state.isValidating);
  const { NavigableItemPopoverComponent, useNavigableItemPopover } = useNavigationContext();
  const { showNavigableItemPopover, setShowNavigableItemPopover, floatingStyles, refs } = useNavigableItemPopover();
  const isLargeVariant = variant === 'large';

  const onRadioFocus = useCallback(() => {
    setShowNavigableItemPopover(true);
  }, [setShowNavigableItemPopover]);

  const onRadioBlur = useCallback(() => {
    onBlur();
    setShowNavigableItemPopover(false);
  }, [onBlur, setShowNavigableItemPopover]);

  useEffect(() => {
    if (error && showNavigableItemPopover) {
      onShowFieldErrorModal?.(true);
    }
  }, [error, showNavigableItemPopover, onShowFieldErrorModal]);

  const inlineErrors =
    isLargeVariant && isWebEDIFormError(error) ? { message: error.description, type: error.type } : undefined;

  return (
    <div ref={isLargeVariant ? undefined : refs.setReference} data-field={name} tabIndex={-1}>
      <Radio.Group
        label={field.label}
        legendVariant={isLargeVariant ? 'large' : 'default'}
        required={required}
        description={field.description ? <Translate i18nKey={field.description} /> : undefined}
      >
        {options.map((option) => (
          <Radio.Item
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            description={option.description || undefined}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            onFocus={isLargeVariant ? undefined : onRadioFocus}
            onBlur={isLargeVariant ? undefined : onRadioBlur}
            disabled={disabled}
            hasErrors={fieldState === 'error'}
          />
        ))}
      </Radio.Group>

      {inlineErrors && <p className="mt-1 text-sm text-red-700">{inlineErrors.message}</p>}

      {!isLargeVariant && showNavigableItemPopover && NavigableItemPopoverComponent && (
        <NavigableItemPopoverComponent
          viewMode={false}
          annotation={error}
          ref={refs.setFloating}
          style={floatingStyles}
          isValidating={isValidating}
          isPendingValidation={fieldState === 'pending-validation'}
          fieldDescription={field.description}
        />
      )}
    </div>
  );
};

SchemableFormRadioGroup.displayName = 'SchemableFormRadioGroup';
